import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../../lib/axios";
import type { RessourceOperationnelleRow } from "../../types/ressourcesOperationnelles";

type DestinationType = "client" | "equipe" | "agent" | "tournee";
type Option = { id: number; label: string };

type Props = {
  open: boolean;
  ressource: RessourceOperationnelleRow | null;
  onClose: () => void;
  onSuccess: () => void;
};

export default function AffecterRessourceModal({
  open,
  ressource,
  onClose,
  onSuccess,
}: Props): React.JSX.Element | null {
  const [destinationType, setDestinationType] = useState<DestinationType>("client");
  const [destinationId, setDestinationId] = useState<number | "">("");
  const [quantite, setQuantite] = useState<number>(1);
  const [commentaire, setCommentaire] = useState("");
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDestinationId("");
    const load = async () => {
      try {
        const endpoint =
          destinationType === "client"
            ? "/clients?page=1&limit=200"
            : destinationType === "equipe"
              ? "/equipes"
              : destinationType === "agent"
                ? "/agents"
                : "/tournees";
        const { data } = await api.get(endpoint);
        const rows = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
        const mapped: Option[] = rows
          .filter((r: unknown): r is Record<string, unknown> => !!r && typeof r === "object")
          .map((r: Record<string, unknown>) => ({
            id: Number(r.id),
            label:
              destinationType === "client"
                ? String((r.nomEntreprise ?? `${r.prenom ?? ""} ${r.nom ?? ""}`.trim()) || `#${r.id}`)
                : destinationType === "agent"
                  ? String(`${r.prenom ?? ""} ${r.nom ?? ""}`.trim() || `#${r.id}`)
                  : destinationType === "tournee"
                    ? String(r.numero ?? `#${r.id}`)
                    : String(r.nom ?? `#${r.id}`),
          }))
          .filter((o: Option) => Number.isFinite(o.id) && o.id > 0);
        setOptions(mapped);
      } catch {
        setOptions([]);
      }
    };
    void load();
  }, [open, destinationType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ressource) return;
    if (!destinationId) return toast.warning("Destination obligatoire.");
    if (!Number.isInteger(quantite) || quantite < 1) return toast.warning("Quantité invalide.");
    const stock = Number(ressource.stockActuel ?? 0);
    if (stock < quantite) return toast.error("STOCK_INSUFFISANT");
    try {
      setLoading(true);
      await api.post(`/ressources-operationnelles/${ressource.id}/affecter`, {
        destinationType,
        destinationId: Number(destinationId),
        quantite,
        commentaire: commentaire.trim() || null,
      });
      toast.success("Affectation enregistrée.");
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: { code?: string; message?: string } } } };
      const code = ax.response?.data?.error?.code;
      if (code === "STOCK_INSUFFISANT") toast.error("STOCK_INSUFFISANT");
      else toast.error(ax.response?.data?.error?.message ?? "Affectation impossible.");
    } finally {
      setLoading(false);
    }
  };

  if (!open || !ressource) return null;
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-xl rounded-2xl border border-slate-700 bg-slate-900 p-5">
        <div className="flex items-start justify-between">
          <h3 className="text-lg font-semibold text-white">Affecter ressource</h3>
          <button type="button" onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-800">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={(e) => void handleSubmit(e)} className="mt-4 space-y-3">
          <p className="text-sm text-slate-300">{ressource.nom} · Stock actuel: {ressource.stockActuel ?? 0}</p>
          <div className="grid gap-3 md:grid-cols-2">
            <select
              value={destinationType}
              onChange={(e) => setDestinationType(e.target.value as DestinationType)}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-white"
            >
              <option value="client">Client</option>
              <option value="equipe">Équipe</option>
              <option value="agent">Agent</option>
              <option value="tournee">Tournée</option>
            </select>
            <input
              type="number"
              min={1}
              value={quantite}
              onChange={(e) => setQuantite(Math.max(1, Number(e.target.value) || 1))}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-white"
              placeholder="Quantité"
            />
          </div>
          <select
            value={destinationId === "" ? "" : String(destinationId)}
            onChange={(e) => setDestinationId(e.target.value === "" ? "" : Number(e.target.value))}
            className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-white"
          >
            <option value="">Choisir destination</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
          <textarea
            value={commentaire}
            onChange={(e) => setCommentaire(e.target.value)}
            rows={2}
            className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-white"
            placeholder="Commentaire"
          />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-xl border border-slate-700 px-4 py-2 text-slate-200">
              Annuler
            </button>
            <button disabled={loading} type="submit" className="rounded-xl bg-emerald-600 px-4 py-2 font-semibold text-white disabled:opacity-60">
              Affecter
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

